import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { Check, Clock, X } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { cn } from 'lib/utils';
import { formatElapsed } from 'utils/elapsed';
import {
  ASSIGNMENT_TONE,
  interventionDay,
  knownRefusalReasonLabel,
  type AssignmentState,
} from 'pages/AdminIssues/components/upcomingAssignment';
import type { NotificationItem, NotificationKind } from 'api/notificationsApi';

/**
 * The feed the back-office bell shows. Its own component rather than markup inside the panel: the
 * panel owns the chrome and the read watermark, the list owns how one event reads.
 *
 * **Unread is a visual state, not a tone.** A left rule and a weight, not a colour: the colours
 * here already mean something (emerald accepted, rose refused, amber waiting), and reusing one for
 * "new" would make the two readings fight. The word rides in the row's own text, `sr-only`, so it
 * survives grayscale and a screen reader without replacing the name the row already has.
 */

/**
 * A `switch` of string literals, never a key built from the kind — the repo's static guard.
 * An item with nothing to add (pre-L6 acceptance, no reason, never reminded) falls back to the
 * short sentence plus the store.
 */
const sentence = (t: TFunction, item: NotificationItem): string => {
  const name = item.technicianName ?? t('The technician');
  const store = item.store ?? item.issueKey;
  switch (item.kind) {
    case 'accepted': {
      const day = interventionDay(item.chosenDate);
      return day && item.chosenTime
        ? t('{{name}} accepted · {{store}} · visit {{day}} at {{time}}', { name, store, day, time: item.chosenTime })
        : `${t('{{technician}} accepted the intervention', { technician: name })} · ${store}`;
    }
    case 'declined': {
      const reason = knownRefusalReasonLabel(t, item.refusalReasonCode);
      return reason
        ? t('{{name}} declined · {{store}} · {{reason}}', { name, store, reason })
        : `${t('{{technician}} refused the intervention', { technician: name })} · ${store}`;
    }
    case 'no_answer':
      return item.reminderCount > 0
        ? t('{{name}} did not answer · {{store}} · reminded ×{{count}}', { name, store, count: item.reminderCount })
        : `${t('{{technician}} has not answered yet', { technician: name })} · ${store}`;
  }
};

const ICON: Record<NotificationKind, LucideIcon> = {
  accepted: Check,
  declined: X,
  no_answer: Clock,
};

/**
 * The palette is `ASSIGNMENT_TONE`'s, read through the state each kind means — emerald confirmed,
 * rose refused, amber waiting, zinc nobody. A fourth table of the same colours is a fourth place
 * for amber to stop meaning "waiting on somebody".
 *
 * `received` is the one addition: it is neither an answer nor an absence of one, and sky is what
 * the technician's own list already uses for a new request.
 */
const KIND_STATE: Record<NotificationKind, AssignmentState> = {
  accepted: 'accepted',
  declined: 'declined',
  no_answer: 'pending',
};

const tint = (kind: NotificationKind): string => ASSIGNMENT_TONE[KIND_STATE[kind]];

interface NotificationListProps {
  items: NotificationItem[];
  /** What opening an item does. Omitted where nothing can be opened. */
  onSelect?: (item: NotificationItem) => void;
  emptyTitle: string;
}

export const NotificationList = ({ items, onSelect, emptyTitle }: NotificationListProps) => {
  const { t } = useTranslation();

  if (items.length === 0) {
    return <p className="px-4 py-6 text-center text-[13px] text-muted-foreground">{emptyTitle}</p>;
  }

  return (
    <ul className="divide-y">
      {items.map((item) => {
        const Icon = ICON[item.kind];
        const actionable = !!onSelect;
        const body = (
          <>
            <span
              className={cn(
                'flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-[8px]',
                tint(item.kind)
              )}
              aria-hidden="true"
            >
              <Icon className="h-[15px] w-[15px]" />
            </span>
            <span className="min-w-0 flex-1">
              <span className={cn('block text-[13px]', item.unread ? 'font-semibold' : 'font-normal')}>
                {sentence(t, item)}
                {/* Inside the name, never as an `aria-label`: on the button that would *replace*
                    the name and drop the store and the delay, and on the non-actionable row it
                    would sit on a bare element most assistive tech ignores. */}
                {item.unread && <span className="sr-only"> — {t('Unread')}</span>}
              </span>
              <span className="mt-[1px] block truncate text-[11.5px] text-muted-foreground">
                {/* Server-measured, never derived from the browser's clock. The store is in the
                    sentence; the desk name is not in the feed yet. */}
                {formatElapsed(item.ageSeconds)}
              </span>
            </span>
          </>
        );

        // The left rule carries "unread" on its own, so the state survives a colour-blind reader
        // and a screenshot in grayscale. The word is in the accessible name, not only in the rule.
        const row = cn(
          'flex w-full items-start gap-[11px] border-l-2 px-4 py-[11px] text-left',
          item.unread ? 'border-l-primary bg-primary/[0.04]' : 'border-l-transparent',
          actionable && 'hover:bg-accent'
        );

        return (
          <li key={item.id}>
            {actionable ? (
              <button type="button" onClick={() => onSelect?.(item)} className={row}>
                {body}
              </button>
            ) : (
              <div className={row}>{body}</div>
            )}
          </li>
        );
      })}
    </ul>
  );
};
